import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useWS } from '../contexts/WebSocketContext';
import { agentGroupsAPI, agentsAPI, clientLicensesAPI } from '../services/api';
import { FiPlus, FiEdit2, FiTrash2, FiMonitor, FiLayers, FiSettings, FiWifi, FiWifiOff, FiInfo, FiShield, FiKey } from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import ConfirmDialog from '../components/ConfirmDialog';

const Devices = () => {
  const { user } = useAuth();
  const { subscribe } = useWS();
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [agentGroups, setAgentGroups] = useState([]);
  const [agents, setAgents] = useState([]);
  const [availableLicenses, setAvailableLicenses] = useState([]);
  
  // UI states
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [showCreateAgentModal, setShowCreateAgentModal] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);
  const [showLicenseAssignModal, setShowLicenseAssignModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [editingAgent, setEditingAgent] = useState(null);
  const [managingAgent, setManagingAgent] = useState(null);
  const [assigningLicenseToAgent, setAssigningLicenseToAgent] = useState(null);
  
  // Form states
  const [groupFormData, setGroupFormData] = useState({
    agentGroupName: '',
    description: ''
  });
  const [agentFormData, setAgentFormData] = useState({
    agentName: '',
    agentSerial: '',
    agentOrientation: 'landscape',
    agentPosition: '',
    agentGroupsID: ''
  });

  // Add connection check functionality
  const [connectionStatus, setConnectionStatus] = useState('unknown');
  const [showConnectionCheck, setShowConnectionCheck] = useState(false);

  // License status
  const [licenseStatus, setLicenseStatus] = useState(null);
  const [showLicenseInfo, setShowLicenseInfo] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, id: null, type: null });

  const checkDatabaseConnection = async () => {
    setShowConnectionCheck(true);
    setConnectionStatus('checking');
    
    try {
      // Try a simple API call to test connectivity
      await agentsAPI.getAgents(user.compID);
      setConnectionStatus('connected');
    } catch (error) {
      console.error('Database connection check failed:', error);
      setConnectionStatus('disconnected');
    }
    
    setTimeout(() => {
      setShowConnectionCheck(false);
    }, 2000);
  };

  const fetchLicenseStatus = async () => {
    try {
      const status = await agentsAPI.getLicenseStatus(user.compID);
      setLicenseStatus(status);
    } catch (error) {
      console.error('License status check failed:', error);
      setLicenseStatus({ 
        valid: false, 
        error: 'Unable to check license status',
        totalLimit: 0,
        totalUsed: 0,
        allLicenses: []
      });
    }
  };

  const fetchData = useCallback(async () => {
    if (!user?.compID) {
      console.log('No user or compID found:', user);
      return;
    }
    
    console.log('Fetching data for company:', user.compID);
    
    try {
      const [agentGroupsData, agentsData, licenseStatusData] = await Promise.all([
        agentGroupsAPI.getAgentGroups(user.compID),
        agentsAPI.getAgents(user.compID),
        agentsAPI.getLicenseStatus(user.compID)
      ]);
      
      console.log('Fetched agent groups:', agentGroupsData.length);
      console.log('Fetched agents:', agentsData.length);
      console.log('License status:', licenseStatusData);
      
      setAgentGroups(agentGroupsData);
      setAgents(agentsData);
      setLicenseStatus(licenseStatusData);
      
      // Don't auto-select any group - let user choose
      
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.compID, selectedGroup]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Real-time device online/offline updates via WebSocket
  useEffect(() => {
    const unsub1 = subscribe('device:online', (data) => {
      setAgents(prev => prev.map(a =>
        a.agentID === data.agentID ? { ...a, agentLastOnline: new Date().toISOString() } : a
      ));
    });
    const unsub2 = subscribe('device:offline', (data) => {
      // Agent went offline — the existing isAgentOnline check handles the 5-min threshold
      // Just trigger a re-render by updating the agents array
      setAgents(prev => [...prev]);
    });
    return () => { unsub1(); unsub2(); };
  }, [subscribe]);

  const getAgentsInGroup = () => {
    if (!selectedGroup) return agents.filter(agent => !agent.agentGroupsID);
    return agents.filter(agent => agent.agentGroupsID === selectedGroup.agentGroupsID);
  };

  const isAgentOnline = (lastOnline) => {
    if (!lastOnline) return false;
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
    return new Date(lastOnline) > fiveMinutesAgo;
  };

  // Group management
  const handleCreateGroup = async (e) => {
    e.preventDefault();
    try {
      await agentGroupsAPI.createAgentGroup({
        ...groupFormData,
        compID: user.compID
      });
      fetchData();
      resetGroupForm();
    } catch (error) {
      console.error('Error creating group:', error);
    }
  };

  const handleUpdateGroup = async (e) => {
    e.preventDefault();
    try {
      await agentGroupsAPI.updateAgentGroup(editingGroup.agentGroupsID, {
        ...groupFormData,
        compID: user.compID
      });
      fetchData();
      resetGroupForm();
    } catch (error) {
      console.error('Error updating group:', error);
    }
  };

  const handleDeleteGroup = (groupId) => {
    setConfirmDialog({ isOpen: true, id: groupId, type: 'group' });
  };

  const resetGroupForm = () => {
    setGroupFormData({ agentGroupName: '', description: '' });
    setEditingGroup(null);
    setShowCreateGroupModal(false);
  };

  // Agent management
  const handleCreateAgent = async (e) => {
    e.preventDefault();
    
    // Auto-generate serial if not provided for testing
    const serialNumber = agentFormData.agentSerial || `DEV-${Date.now()}`;
    
    console.log('Creating agent with data:', { ...agentFormData, agentSerial: serialNumber });
    console.log('User compID:', user.compID);
    
    if (!agentFormData.agentName.trim()) {
      toast.error('Device name is required');
      return;
    }
    
    try {
      const payload = {
        ...agentFormData,
        agentSerial: serialNumber,
        compID: user.compID,
        agentGroupsID: selectedGroup?.agentGroupsID || agentFormData.agentGroupsID || null
      };
      console.log('Payload being sent:', payload);
      
      const result = await agentsAPI.createAgent(payload);
      console.log('Agent created successfully:', result);
      
      await fetchData();
      resetAgentForm();
      toast.success('Device created successfully!');
    } catch (error) {
      console.error('Error creating agent:', error);
      console.error('Error response:', error.response?.data);
      toast.error(`Error creating device: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleUpdateAgent = async (e) => {
    e.preventDefault();
    console.log('Updating agent:', editingAgent.agentID, 'with data:', agentFormData);
    
    try {
      const payload = {
        ...agentFormData,
        compID: user.compID,
        agentGroupsID: agentFormData.agentGroupsID || null
      };
      console.log('Update payload:', payload);
      
      const result = await agentsAPI.updateAgent(editingAgent.agentID, payload);
      console.log('Agent updated successfully:', result);
      
      await fetchData();
      resetAgentForm();
      toast.success('Device updated successfully!');
    } catch (error) {
      console.error('Error updating agent:', error);
      console.error('Error response:', error.response?.data);
      toast.error(`Error updating device: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleDeleteAgent = (agentId) => {
    console.log('Attempting to delete agent:', agentId, 'for company:', user.compID);
    setConfirmDialog({ isOpen: true, id: agentId, type: 'agent' });
  };

  const confirmDelete = async () => {
    const { id, type } = confirmDialog;
    try {
      if (type === 'group') {
        await agentGroupsAPI.deleteAgentGroup(user.compID, id);
        if (selectedGroup && selectedGroup.agentGroupsID === id) {
          setSelectedGroup(null);
        }
        toast.success('Device group deleted successfully!');
      } else if (type === 'agent') {
        console.log('Deleting agent:', id);
        const result = await agentsAPI.deleteAgent(user.compID, id);
        console.log('Agent deleted successfully:', result);
        toast.success('Device deleted successfully!');
      }
      await fetchData();
      setConfirmDialog({ isOpen: false, id: null, type: null });
    } catch (error) {
      console.error(`Error deleting ${type}:`, error);
      console.error('Error response:', error.response?.data);
      toast.error(`Error deleting ${type}: ${error.response?.data?.error || error.message}`);
      setConfirmDialog({ isOpen: false, id: null, type: null });
    }
  };

  const handleToggleActive = async (agent) => {
    try {
      // If activating an agent, check license limits first
      if (!agent.agentActive && licenseStatus && !licenseStatus.valid) {
        toast.error(`Cannot activate device: ${licenseStatus.error}`);
        return;
      }

      await agentsAPI.updateAgent(agent.agentID, {
        ...agent,
        compID: user.compID,
        agentActive: !agent.agentActive
      });
      fetchData();
    } catch (error) {
      console.error('Error toggling agent status:', error);
      // Check if it's a license limit error
      if (error.response?.status === 403 && error.response?.data?.licenseInfo) {
        const { licenseInfo } = error.response.data;
        toast.error(`Cannot activate device: ${error.response.data.error}\n\nLicense allows ${licenseInfo.deviceLimit} devices, currently using ${licenseInfo.usedDevices}.`);
      } else {
        toast.error('Error updating device status. Please try again.');
      }
    }
  };

  // License management
  const fetchAvailableLicenses = async () => {
    if (!user?.compID) return;
    
    try {
      const licenses = await clientLicensesAPI.getLicenses(user.compID);
      // Only show active licenses that haven't expired
      const activeLicenses = licenses.filter(license => 
        license.isActive && new Date(license.licenseEnd) >= new Date()
      );
      setAvailableLicenses(activeLicenses);
    } catch (error) {
      console.error('Error fetching available licenses:', error);
      setAvailableLicenses([]);
    }
  };

  const handleAssignLicense = async (agentId, licenseId) => {
    try {
      // Get the current agent data first
      const agent = agents.find(a => a.agentID === agentId);
      if (!agent) {
        toast.error('Agent not found');
        return;
      }

      // Update the agent with the new license
      await agentsAPI.updateAgent(agentId, {
        ...agent,
        compID: user.compID,
        agentLicense: licenseId
      });
      
      toast.success('License assigned successfully!');
      await fetchData(); // Refresh the data
      setShowLicenseAssignModal(false);
      setAssigningLicenseToAgent(null);
    } catch (error) {
      console.error('Error assigning license:', error);
      toast.error(`Error assigning license: ${error.response?.data?.error || error.message}`);
    }
  };

  const resetAgentForm = () => {
    setAgentFormData({
      agentName: '',
      agentSerial: '',
      agentOrientation: 'landscape',
      agentPosition: '',
      agentGroupsID: ''
    });
    setEditingAgent(null);
    setShowCreateAgentModal(false);
  };

  const startEditGroup = (group) => {
    setEditingGroup(group);
    setGroupFormData({
      agentGroupName: group.agentGroupName,
      description: group.description || ''
    });
    setShowCreateGroupModal(true);
  };

  const startEditAgent = (agent) => {
    setEditingAgent(agent);
    setAgentFormData({
      agentName: agent.agentName,
      agentSerial: agent.agentSerial,
      agentOrientation: agent.agentOrientation,
      agentPosition: agent.agentPosition || '',
      agentGroupsID: agent.agentGroupsID || ''
    });
    setShowCreateAgentModal(true);
  };

  const startAssignLicense = (agent) => {
    setAssigningLicenseToAgent(agent);
    fetchAvailableLicenses();
    setShowLicenseAssignModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading devices...</p>
        </div>
      </div>
    );
  }

  // Empty state component
  const EmptyState = ({ title, description, icon: Icon, actionText, onAction }) => (
    <div className="text-center py-12">
      <Icon className="mx-auto h-12 w-12 text-gray-400" />
      <h3 className="mt-2 text-sm font-medium text-gray-900">{title}</h3>
      <p className="mt-1 text-sm text-gray-500">{description}</p>
      {onAction && (
        <div className="mt-6">
          <button
            onClick={onAction}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            <FiPlus className="-ml-1 mr-2 h-4 w-4" />
            {actionText}
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="h-screen flex flex-col p-6">
      {/* Header */}
      <div className="mb-6 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Device Management</h1>
            <p className="mt-2 text-gray-600">
              Organize and manage your digital signage devices
            </p>
          </div>
          
          {/* License Status, Connection Status & Buttons */}
          <div className="flex items-center space-x-3">
            {/* License Status */}
            {licenseStatus && (
              <div className={`flex items-center space-x-2 px-3 py-1 rounded-full text-sm ${
                licenseStatus.valid 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              }`}>
                <FiShield className="w-4 h-4" />
                <span>
                  License: {licenseStatus.totalUsed}/{licenseStatus.totalLimit} devices
                </span>
                <button
                  onClick={() => setShowLicenseInfo(!showLicenseInfo)}
                  className="ml-1 p-1 hover:bg-black hover:bg-opacity-10 rounded"
                >
                  <FiInfo className="w-3 h-3" />
                </button>
              </div>
            )}
            
            {connectionStatus !== 'unknown' && (
              <div className={`flex items-center space-x-2 px-3 py-1 rounded-full text-sm ${
                connectionStatus === 'connected' 
                  ? 'bg-green-100 text-green-800' 
                  : connectionStatus === 'disconnected'
                  ? 'bg-red-100 text-red-800'
                  : 'bg-yellow-100 text-yellow-800'
              }`}>
                <div className={`w-2 h-2 rounded-full ${
                  connectionStatus === 'connected' 
                    ? 'bg-green-500' 
                    : connectionStatus === 'disconnected'
                    ? 'bg-red-500'
                    : 'bg-yellow-500'
                }`}></div>
                <span>
                  {connectionStatus === 'connected' 
                    ? 'Database Connected' 
                    : connectionStatus === 'disconnected'
                    ? 'Database Offline'
                    : 'Checking...'
                  }
                </span>
              </div>
            )}
            
            <button
              onClick={checkDatabaseConnection}
              disabled={showConnectionCheck}
              className="px-3 py-2 bg-gray-600 text-white text-sm rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50"
            >
              {showConnectionCheck ? 'Checking...' : 'Check Connection'}
            </button>
          </div>
        </div>

        {/* License Information Popup */}
        {showLicenseInfo && licenseStatus && (
          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start justify-between">  
              <div>
                <h3 className="font-medium text-blue-900">License Information</h3>
                <div className="mt-2 text-sm text-blue-800">
                  <p><strong>Total Device Limit:</strong> {licenseStatus.totalLimit}</p>
                  <p><strong>Total Devices Used:</strong> {licenseStatus.totalUsed}</p>
                  <p><strong>Available:</strong> {licenseStatus.totalLimit - licenseStatus.totalUsed}</p>
                  {licenseStatus.allLicenses && licenseStatus.allLicenses.length > 0 && (
                    <div className="mt-3">
                      <p className="font-medium">License Details:</p>
                      {licenseStatus.allLicenses.map(license => (
                        <div key={license.licenseID} className="mt-2 p-2 bg-white rounded border">
                          <p><strong>Serial:</strong> {license.licenseSerial}</p>
                          <p><strong>Usage:</strong> {license.usedDevices}/{license.deviceLimit} devices</p>
                          <p><strong>Valid Until:</strong> {new Date(license.licenseEnd).toLocaleDateString()}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  {licenseStatus.error && (
                    <p className="text-red-600 mt-2"><strong>Warning:</strong> {licenseStatus.error}</p>
                  )}
                </div>
              </div>
              <button
                onClick={() => setShowLicenseInfo(false)}
                className="text-blue-400 hover:text-blue-600"
              >
                ×
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Main Layout */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-5 gap-6 min-h-0">
        {/* Left Panel - Device Groups */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow flex flex-col">
          <div className="p-4 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <FiLayers className="h-5 w-5 text-blue-600" />
                <h2 className="text-lg font-semibold text-gray-900">Device Groups</h2>
              </div>
              <button
                onClick={() => setShowCreateGroupModal(true)}
                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                title="Add Group"
              >
                <FiPlus className="h-4 w-4" />
              </button>
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto min-h-0">
            {/* Ungrouped devices option */}
            <div
              onClick={() => setSelectedGroup(null)}
              className={`p-4 border-b border-gray-100 cursor-pointer transition-colors ${
                !selectedGroup ? 'bg-blue-50 border-l-4 border-l-blue-500' : 'hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-gray-900">Ungrouped Devices</h3>
                  <p className="text-sm text-gray-500">Devices not assigned to any group</p>
                </div>
                <span className="text-sm text-gray-500">
                  {agents.filter(a => !a.agentGroupsID).length}
                </span>
              </div>
            </div>

            {agentGroups.length === 0 ? (
              <div className="p-4">
                <EmptyState
                  title="No Device Groups"
                  description="Create your first device group to organize your devices."
                  icon={FiLayers}
                  actionText="Create Group"
                  onAction={() => setShowCreateGroupModal(true)}
                />
              </div>
            ) : (
              agentGroups.map(group => {
                const groupAgents = agents.filter(agent => agent.agentGroupsID === group.agentGroupsID);
                const onlineCount = groupAgents.filter(agent => isAgentOnline(agent.agentLastOnline)).length;
                
                return (
                  <div
                    key={group.agentGroupsID}
                    onClick={() => setSelectedGroup(group)}
                    className={`p-4 border-b border-gray-100 cursor-pointer transition-colors ${
                      selectedGroup?.agentGroupsID === group.agentGroupsID 
                        ? 'bg-blue-50 border-l-4 border-l-blue-500' 
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">{group.agentGroupName}</h3>
                        <p className="text-sm text-gray-500">{group.description}</p>
                        <div className="flex items-center space-x-4 mt-1">
                          <span className="text-xs text-gray-500">
                            {groupAgents.length} devices
                          </span>
                          <span className="text-xs text-green-600">
                            {onlineCount} online
                          </span>
                        </div>
                      </div>
                    <div className="flex space-x-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          startEditGroup(group);
                        }}
                        className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                      >
                        <FiEdit2 className="h-3 w-3" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteGroup(group.agentGroupsID);
                        }}
                        className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                      >
                        <FiTrash2 className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            }))}
          </div>
        </div>

        {/* Right Panel - Devices in Selected Group */}
        <div className="lg:col-span-3 bg-white rounded-lg shadow flex flex-col">
          <div className="p-4 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <FiMonitor className="h-5 w-5 text-green-600" />
                <h2 className="text-lg font-semibold text-gray-900">
                  {selectedGroup ? selectedGroup.agentGroupName : 'Ungrouped Devices'}
                </h2>
              </div>
              <button
                onClick={() => setShowCreateAgentModal(true)}
                className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-1"
              >
                <FiPlus className="h-3 w-3" />
                <span>Add Device</span>
              </button>
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto min-h-0 p-4">
            {getAgentsInGroup().length === 0 ? (
              <EmptyState
                title="No Devices"
                description={selectedGroup 
                  ? 'No devices assigned to this group yet.' 
                  : 'No ungrouped devices found.'
                }
                icon={FiMonitor}
                actionText="Add First Device"
                onAction={() => setShowCreateAgentModal(true)}
              />
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {getAgentsInGroup().map(agent => (
                  <div key={agent.agentID} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className="flex items-center">
                          {isAgentOnline(agent.agentLastOnline) ? (
                            <FiWifi className="h-5 w-5 text-green-500" />
                          ) : (
                            <FiWifiOff className="h-5 w-5 text-gray-400" />
                          )}
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">{agent.agentName}</h4>
                          <p className="text-sm text-gray-500">Serial: {agent.agentSerial}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          agent.agentActive 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {agent.agentActive ? 'Active' : 'Inactive'}
                        </span>
                        <button
                          onClick={() => {
                            setManagingAgent(agent);
                            setShowManageModal(true);
                          }}
                          className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors"
                        >
                          <FiSettings className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => startEditAgent(agent)}
                          className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors"
                        >
                          <FiEdit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteAgent(agent.agentID)}
                          className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"
                        >
                          <FiTrash2 className="h-4 w-4" />
                        </button>
                        {!agent.licenseSerial && (
                          <button
                            onClick={() => startAssignLicense(agent)}
                            className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors"
                            title="Assign License"
                          >
                            <FiKey className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Position:</span>
                        <span className="ml-2 text-gray-900">{agent.agentPosition || 'Not set'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Orientation:</span>
                        <span className="ml-2 text-gray-900 capitalize">{agent.agentOrientation}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Media Group:</span>
                        <span className="ml-2 text-gray-900">{agent.mediaGroupName || 'Not assigned'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">License:</span>
                        <span className={`ml-2 ${agent.licenseSerial ? 'text-green-600' : 'text-gray-400'}`}>
                          {agent.licenseSerial || 'Not assigned'}
                        </span>
                      </div>
                      <div className="col-span-2">
                        <span className="text-gray-500">Last Online:</span>
                        <span className="ml-2 text-gray-900">
                          {agent.agentLastOnline 
                            ? new Date(agent.agentLastOnline).toLocaleString()
                            : 'Never'
                          }
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Group Create/Edit Modal */}
      {showCreateGroupModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              {editingGroup ? 'Edit Device Group' : 'Create Device Group'}
            </h3>
            <form onSubmit={editingGroup ? handleUpdateGroup : handleCreateGroup}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Group Name
                  </label>
                  <input
                    type="text"
                    value={groupFormData.agentGroupName}
                    onChange={(e) => setGroupFormData({...groupFormData, agentGroupName: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={groupFormData.description}
                    onChange={(e) => setGroupFormData({...groupFormData, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows="3"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={resetGroupForm}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingGroup ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Agent Create/Edit Modal */}
      {showCreateAgentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              {editingAgent ? 'Edit Device' : 'Add Device'}
            </h3>
            <form onSubmit={(e) => {
              console.log('Form submitted');
              if (editingAgent) {
                handleUpdateAgent(e);
              } else {
                handleCreateAgent(e);
              }
            }}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Device Name
                  </label>
                  <input
                    type="text"
                    value={agentFormData.agentName}
                    onChange={(e) => setAgentFormData({...agentFormData, agentName: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Serial Number (optional - will auto-generate if empty)
                  </label>
                  <input
                    type="text"
                    value={agentFormData.agentSerial}
                    onChange={(e) => setAgentFormData({...agentFormData, agentSerial: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Leave empty for auto-generation"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Position/Location
                  </label>
                  <input
                    type="text"
                    value={agentFormData.agentPosition}
                    onChange={(e) => setAgentFormData({...agentFormData, agentPosition: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Lobby, Conference Room A"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Group
                  </label>
                  <select
                    value={agentFormData.agentGroupsID}
                    onChange={(e) => setAgentFormData({...agentFormData, agentGroupsID: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select a group</option>
                    {agentGroups.map(group => (
                      <option key={group.agentGroupsID} value={group.agentGroupsID}>
                        {group.agentGroupName}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Orientation
                  </label>
                  <select
                    value={agentFormData.agentOrientation}
                    onChange={(e) => setAgentFormData({...agentFormData, agentOrientation: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="landscape">Landscape</option>
                    <option value="portrait">Portrait</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={resetAgentForm}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  onClick={(e) => {
                    console.log('Submit button clicked');
                    console.log('Form data:', agentFormData);
                    console.log('User:', user);
                  }}
                >
                  {editingAgent ? 'Update' : 'Add'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Agent Management Modal */}
      {showManageModal && managingAgent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              Manage Device: {managingAgent.agentName}
            </h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <span className="text-sm font-medium">Status</span>
                <button
                  onClick={() => {
                    handleToggleActive(managingAgent);
                    setShowManageModal(false);
                  }}
                  disabled={!managingAgent.agentActive && licenseStatus && !licenseStatus.valid}
                  className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                    managingAgent.agentActive
                      ? 'bg-red-100 text-red-700 hover:bg-red-200'
                      : (!licenseStatus || licenseStatus.valid)
                      ? 'bg-green-100 text-green-700 hover:bg-green-200'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                  title={!managingAgent.agentActive && licenseStatus && !licenseStatus.valid 
                    ? licenseStatus.error 
                    : undefined
                  }
                >
                  {managingAgent.agentActive ? 'Deactivate' : 'Activate'}
                  {!managingAgent.agentActive && licenseStatus && !licenseStatus.valid && (
                    <span className="ml-1 text-xs">(License Limit)</span>
                  )}
                </button>
              </div>

              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <span className="text-sm font-medium">License</span>
                  <p className="text-xs text-gray-500 mt-1">
                    {managingAgent.licenseSerial || 'No license assigned'}
                  </p>
                </div>
                {!managingAgent.licenseSerial && (
                  <button
                    onClick={() => {
                      startAssignLicense(managingAgent);
                      setShowManageModal(false);
                    }}
                    className="flex items-center space-x-1 px-3 py-1 bg-blue-100 text-blue-700 hover:bg-blue-200 rounded text-sm font-medium transition-colors"
                  >
                    <FiKey className="h-3 w-3" />
                    <span>Assign License</span>
                  </button>
                )}
              </div>
            </div>
            
            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowManageModal(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* License Assignment Modal */}
      {showLicenseAssignModal && assigningLicenseToAgent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              Assign License to Device: {assigningLicenseToAgent.agentName}
            </h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Available Licenses
              </label>
              <select
                id="licenseSelect"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                defaultValue=""
              >
                <option value="">Select a license</option>
                {availableLicenses.map(license => (
                  <option key={license.licenseID} value={license.licenseID}>
                    {license.licenseSerial} - {license.deviceLimit} devices (Expires: {new Date(license.licenseEnd).toLocaleDateString()})
                  </option>
                ))}
              </select>
              {availableLicenses.length === 0 && (
                <p className="text-sm text-gray-500 mt-2">No active licenses available</p>
              )}
            </div>

            {assigningLicenseToAgent.licenseSerial && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>Current License:</strong> {assigningLicenseToAgent.licenseSerial}
                </p>
                <p className="text-xs text-yellow-600 mt-1">
                  Assigning a new license will replace the current one.
                </p>
              </div>
            )}
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowLicenseAssignModal(false);
                  setAssigningLicenseToAgent(null);
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const selectedLicense = document.getElementById('licenseSelect').value;
                  if (selectedLicense) {
                    handleAssignLicense(assigningLicenseToAgent.agentID, selectedLicense);
                  } else {
                    toast.error('Please select a license');
                  }
                }}
                disabled={availableLicenses.length === 0}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                Assign License
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Connection Check Modal */}
      {showConnectionCheck && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              Database Connection Status
            </h3>
            <p className="text-center text-sm text-gray-500 mb-4">
              {connectionStatus === 'checking' && 'Checking database connection...'}
              {connectionStatus === 'connected' && 'Database connection is active.'}
              {connectionStatus === 'disconnected' && 'Database connection is inactive.'}
            </p>
            <div className="flex justify-center space-x-3">
              <button
                onClick={() => {
                  checkDatabaseConnection();
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Retry Connection
              </button>
              <button
                onClick={() => setShowConnectionCheck(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false, id: null, type: null })}
        onConfirm={confirmDelete}
        title={confirmDialog.type === 'group' ? 'Delete Device Group' : 'Delete Device'}
        message={`Are you sure you want to delete this ${confirmDialog.type === 'group' ? 'device group' : 'device'}? This action cannot be undone.`}
      />
    </div>
  );
};

export default Devices;
